import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import type * as RPNInput from 'react-phone-number-input';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PhoneInput } from '@/components/ui/phone-input';

interface FormPhoneFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  defaultCountry?: RPNInput.Country;
  /** Optional control rendered on the right of the label (e.g. Edit). */
  labelAction?: React.ReactNode;
}

export const FormPhoneField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder = 'Enter phone number',
  description,
  required,
  className,
  inputClassName,
  disabled,
  defaultCountry,
  labelAction,
}: FormPhoneFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <div className="flex items-center justify-between gap-2">
          <FormLabel>
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
          {labelAction}
        </div>
        <FormControl>
          <PhoneInput
            placeholder={placeholder}
            value={field.value as RPNInput.Value | undefined}
            onChange={field.onChange}
            onBlur={field.onBlur}
            className={inputClassName}
            disabled={disabled}
            defaultCountry={defaultCountry}
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);
FormPhoneField.displayName = 'FormPhoneField';
